import type { MeasurementSource } from "./measurement";

/**
 * Renovation Features — the units of renovation scope (spec Section 19),
 * and the future attachment point for Assemblies, quantities, costs, and
 * scheduling.
 *
 * Two entities:
 *
 *  - FEATURE DEFINITION: reusable library entry ("Replace Toilet") with a
 *    default action. Seeded + user-created; archive hides from pickers.
 *
 *  - PROJECT FEATURE: a specific occurrence of scope in a project.
 *    COPY-ON-CREATE: the instance copies the definition's name and action
 *    at creation and owns them thereafter — renaming either side never
 *    rewrites the other. The definition link is kept so future Assemblies
 *    can be suggested per definition, and is OPTIONAL so one-off custom
 *    scope needs no library entry.
 *
 * Attachment: projectId always set; at most one of roomId/floorId set
 * (both null = project-level scope such as "Replace Roof").
 *
 * Action: a structured semantic hook (never parsed from names) for future
 * Existing/Proposed modeling, demolition scope, plan-diff generation,
 * assembly selection, and reporting.
 *
 * Quantity (Phase 3): optional, manual, DESCRIPTIVE ONLY — it drives no
 * cost. Exact integer hundredths + unit + provenance so assembly-rule and
 * geometry sources can join later without redesign (spec Section 21).
 */

export type FeatureAction =
  | "install"
  | "replace"
  | "remove"
  | "repair"
  | "refinish"
  | "keep"
  | "other";

export const FEATURE_ACTIONS: ReadonlyArray<{ id: FeatureAction; displayName: string }> = [
  { id: "install", displayName: "Install" },
  { id: "replace", displayName: "Replace" },
  { id: "remove", displayName: "Remove" },
  { id: "repair", displayName: "Repair" },
  { id: "refinish", displayName: "Refinish" },
  { id: "keep", displayName: "Keep" },
  { id: "other", displayName: "Other" },
];

/** Fixed Phase 3 quantity/measurement units (constants; a configurable
 *  units table is deferred until material package units arrive, Phase 6). */
export type QuantityUnit =
  | "each"
  | "sqft"
  | "lf"
  | "ft"
  | "sy"
  | "gallon"
  | "sheet"
  | "case"
  | "box"
  | "bag"
  | "roll"
  | "hour"
  /** Spec §90: a roofing square — 100 square feet of roof, the unit a roofer prices. */
  | "square";

export const QUANTITY_UNITS: ReadonlyArray<{ id: QuantityUnit; displayName: string }> = [
  { id: "each", displayName: "each" },
  { id: "sqft", displayName: "sqft" },
  { id: "lf", displayName: "linear ft" },
  { id: "ft", displayName: "ft" },
  { id: "sy", displayName: "sq yd" },
  { id: "gallon", displayName: "gallon" },
  { id: "sheet", displayName: "sheet" },
  { id: "case", displayName: "case" },
  { id: "box", displayName: "box" },
  { id: "bag", displayName: "bag" },
  { id: "roll", displayName: "roll" },
  { id: "hour", displayName: "hour" },
  { id: "square", displayName: "roof squares" },
];

export interface FeatureDefinition {
  id: string;
  displayName: string;
  defaultAction: FeatureAction;
  sortOrder: number;
  isSystem: boolean;
  archivedAt: string | null;
}

/**
 * Phase 7 geometry quantity bindings: the feature's quantity is DERIVED
 * from its room's floorplan geometry. More sources (wall area, opening
 * counts, design objects) join this list in later phases.
 */
export type FeatureGeometryBinding =
  | "room-floor-area"
  | "room-perimeter"
  /** Spec §89: gross wall surface — the room's perimeter × its wall height. */
  | "room-wall-area"
  /** Spec §89: ceiling surface — a flat ceiling's area is the floor's. */
  | "room-ceiling-area"
  | "objects-count"
  | "objects-run-length"
  | "objects-area"
  | "change-wall-length"
  | "change-count"
  /** Spec §82: a project finish, measured over the rooms it covers. */
  | "project-finish-area"
  /** Spec §85: doors this plan is replacing in the openings they already
   *  occupy — counted, never measured. */
  | "opening-replacement-count";

export const FEATURE_GEOMETRY_BINDINGS: ReadonlyArray<{ id: FeatureGeometryBinding; displayName: string; unit: QuantityUnit }> = [
  { id: "room-floor-area", displayName: "Room floor area", unit: "sqft" },
  { id: "room-perimeter", displayName: "Room perimeter", unit: "lf" },
  { id: "room-wall-area", displayName: "Room wall area", unit: "sqft" },
  { id: "room-ceiling-area", displayName: "Room ceiling area", unit: "sqft" },
  { id: "objects-count", displayName: "Design objects (count)", unit: "each" },
  { id: "objects-run-length", displayName: "Design objects (run length)", unit: "lf" },
  { id: "objects-area", displayName: "Design objects (area)", unit: "sqft" },
  { id: "change-wall-length", displayName: "Change analysis (wall LF)", unit: "lf" },
  { id: "change-count", displayName: "Change analysis (count)", unit: "each" },
  { id: "project-finish-area", displayName: "Project finish coverage", unit: "sqft" },
  { id: "opening-replacement-count", displayName: "Doors replaced in place (count)", unit: "each" },
];

export function geometryBindingUnit(binding: FeatureGeometryBinding): QuantityUnit {
  switch (binding) {
    case "room-floor-area":
    case "room-wall-area":
    case "room-ceiling-area":
    case "objects-area":
    case "project-finish-area":
      return "sqft";
    case "room-perimeter":
    case "objects-run-length":
    case "change-wall-length":
      return "lf";
    case "objects-count":
    case "change-count":
    case "opening-replacement-count":
      return "each";
  }
}

/** Bindings measured from the feature's own ROOM (typed size or drawn geometry). */
export function isRoomBinding(
  binding: FeatureGeometryBinding | null,
): binding is "room-floor-area" | "room-perimeter" | "room-wall-area" | "room-ceiling-area" {
  return binding === "room-floor-area" || binding === "room-perimeter" || binding === "room-wall-area" || binding === "room-ceiling-area";
}

/** Bindings whose quantity aggregates over bound plan objects (Phase 8). */
export function isObjectBinding(binding: FeatureGeometryBinding | null): binding is "objects-count" | "objects-run-length" | "objects-area" {
  return binding === "objects-count" || binding === "objects-run-length" || binding === "objects-area";
}

/** Bindings whose quantity is maintained by the Phase 9 change analysis
 *  (Existing ↔ Proposed diff). Room-* refresh paths must skip them, exactly
 *  like object bindings. */
export function isChangeBinding(binding: FeatureGeometryBinding | null): binding is "change-wall-length" | "change-count" {
  return binding === "change-wall-length" || binding === "change-count";
}

/**
 * Spec §85: the quantity is the number of doors the plan replaces IN
 * PLACE. Like the change and object bindings, it is owned by its own
 * reconciler — room-* refresh paths and the §84 room re-derivation must
 * skip it, because a door belongs to two rooms and to neither.
 */
export function isOpeningReplacementBinding(binding: FeatureGeometryBinding | null): binding is "opening-replacement-count" {
  return binding === "opening-replacement-count";
}

export interface EffectiveFeatureQuantity {
  valueX100: number | null;
  unit: QuantityUnit | null;
  /** 'package' — a default that came with a package or standard (spec §90-27). */
  source: "manual" | "geometry" | "override" | "default" | "package";
  /** Calculated geometry value (visible even when overridden). */
  geometryX100: number | null;
  manualX100: number | null;
}

/**
 * Effective feature quantity (the assembly driving quantity):
 *   binding + source 'override' → manual value (geometry still shown)
 *   binding + geometry value     → geometry value, unit from binding
 *   otherwise                    → manual value
 * Nothing destructive: manual and geometry values live in separate columns.
 */
export function effectiveFeatureQuantity(
  f: Pick<ProjectFeature, "quantityX100" | "quantityUnit" | "quantitySource" | "quantityGeometryBinding" | "quantityGeometryX100">,
): EffectiveFeatureQuantity {
  const manual = f.quantityX100;
  if (f.quantityGeometryBinding) {
    const unit = geometryBindingUnit(f.quantityGeometryBinding);
    if (f.quantitySource === "override" && manual !== null) {
      return { valueX100: manual, unit: f.quantityUnit ?? unit, source: "override", geometryX100: f.quantityGeometryX100, manualX100: manual };
    }
    if (f.quantityGeometryX100 !== null) {
      return { valueX100: f.quantityGeometryX100, unit, source: "geometry", geometryX100: f.quantityGeometryX100, manualX100: manual };
    }
  }
  if (manual !== null) {
    const source = f.quantitySource === "override" ? "override" : f.quantitySource === "package" ? "package" : "manual";
    return { valueX100: manual, unit: f.quantityUnit, source, geometryX100: f.quantityGeometryX100, manualX100: manual };
  }
  return { valueX100: null, unit: null, source: "default", geometryX100: f.quantityGeometryX100, manualX100: null };
}

export interface ProjectFeature {
  id: string;
  projectId: string;
  /** Scenario scope: NULL = Common Project Scope (every plan); else the owning Design. */
  designId: string | null;
  /** Lineage across duplicated designs (root feature id; null = original). */
  originId: string | null;
  /** At most one of roomId/floorId is set; both null = project-level. */
  roomId: string | null;
  floorId: string | null;
  /** Optional library link (null = ad-hoc custom feature). */
  featureDefinitionId: string | null;
  /** Owned by the instance (copied from the definition at creation). */
  name: string;
  action: FeatureAction;
  /** Optional manual quantity, value × 100 (the override when bound to geometry). */
  quantityX100: number | null;
  quantityUnit: QuantityUnit | null;
  quantitySource: MeasurementSource | null;
  /** Phase 7: derive the quantity from the room's geometry (null = manual). */
  quantityGeometryBinding: FeatureGeometryBinding | null;
  /** Cached geometry-derived quantity (×100) for the binding's unit. */
  quantityGeometryX100: number | null;
  /**
   * Spec §82: the durable identity of a feature the FINISH system owns
   * ("walls:wall-paint"); null for ordinary scope. What makes it safe to say
   * "this room inherits the project's finishes" rather than copying them.
   */
  finishKey: string | null;
  notes: string;
  sortOrder: number;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ProjectFeatureInput {
  /** Scenario scope: NULL/omitted = Common Project Scope. */
  designId?: string | null;
  roomId: string | null;
  floorId: string | null;
  featureDefinitionId: string | null;
  name: string;
  action: FeatureAction;
  quantityX100: number | null;
  quantityUnit: QuantityUnit | null;
  notes: string;
}

export function validateProjectFeatureInput(input: ProjectFeatureInput): string[] {
  const problems: string[] = [];
  if (input.name.trim() === "") {
    problems.push("Feature name is required.");
  }
  if (input.roomId !== null && input.floorId !== null) {
    problems.push("A feature attaches to a room OR a floor, not both.");
  }
  if (input.quantityX100 !== null) {
    if (!Number.isSafeInteger(input.quantityX100) || input.quantityX100 < 0) {
      problems.push("Quantity must be a valid non-negative number.");
    }
    if (input.quantityUnit === null) {
      problems.push("A unit is required when a quantity is entered.");
    }
  }
  return problems;
}
